import { Component, type ReactNode } from "react"

type State = { error: Error | null }

/**
 * Catches render-time exceptions anywhere under it and renders a friendly
 * fallback instead of the React white-screen. Logs to console.error so the
 * error still surfaces in Sentry / browser devtools. Auto-resets when the
 * URL changes so users aren't stuck on the fallback if they navigate.
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error("[ErrorBoundary]", error, info)

    // ── Stale-chunk auto-recover ────────────────────────────────────────
    // After a deploy, browsers that cached the previous index.html try to
    // fetch chunk hashes that no longer exist. Vite raises one of:
    //   • "Failed to fetch dynamically imported module: …/CloudLayout-*.js"
    //   • ChunkLoadError
    //   • "Loading chunk … failed"
    // The fix is a single hard reload to pull the new index.html. Guard
    // with sessionStorage so a real (non-chunk) error doesn't spin reloads.
    const msg = String(error?.message ?? "")
    const isChunkError =
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /ChunkLoadError/i.test(msg) ||
      /Loading (CSS )?chunk \S+ failed/i.test(msg) ||
      (error as { name?: string })?.name === "ChunkLoadError"

    if (isChunkError) {
      const KEY = "snow_chunk_reload_at"
      const last = Number(sessionStorage.getItem(KEY) ?? 0)
      const now  = Date.now()
      if (now - last > 30_000) {
        sessionStorage.setItem(KEY, String(now))
        window.location.reload()
      }
    }
  }

  componentDidMount() {
    this._onPopState = () => {
      if (this.state.error) this.setState({ error: null })
    }
    window.addEventListener("popstate", this._onPopState)
  }

  componentWillUnmount() {
    if (this._onPopState) window.removeEventListener("popstate", this._onPopState)
  }

  private _onPopState?: () => void

  reset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children

    const message = this.state.error.message || "Something went wrong."

    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="max-w-lg w-full bg-white rounded-xl border border-zinc-200 p-6 space-y-4">
          <div>
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Something went wrong</p>
            <h1 className="text-lg font-semibold text-zinc-900 mt-1">This page hit an unexpected error.</h1>
            <p className="text-sm text-zinc-500 mt-2">
              The rest of the app is still working. Try reloading this page, or head back to your dashboard.
            </p>
          </div>

          <pre className="text-[11px] font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-3 overflow-x-auto whitespace-pre-wrap">
            {message}
          </pre>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => { this.reset(); window.location.reload() }}
              className="rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold px-3 py-1.5"
            >
              Reload page
            </button>
            <a
              href="/app/dashboard"
              onClick={this.reset}
              className="rounded-md border border-zinc-200 hover:bg-zinc-50 text-sm font-medium text-zinc-700 px-3 py-1.5"
            >
              Back to dashboard
            </a>
            <a
              href="mailto:support@snowmed.health?subject=Dashboard%20error"
              className="ml-auto text-sm text-zinc-500 hover:text-zinc-900 self-center"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    )
  }
}
